import { computeContainRect } from '../domain/liveView/mediaContainGeometry';

export type ImageBoxSize = {
  width: number;
  height: number;
};

export type DisplayedImageBox = ImageBoxSize & {
  left: number;
  top: number;
};

function finitePositive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

/**
 * Scale a 0-1 camera-normalized polygon onto an image box.
 * The snapshot must already preserve its native aspect ratio; this helper
 * only maps normalized coordinates onto that displayed box. Fail closed.
 */
export function scaleNormalizedPolygonToImageBox(
  polygon: number[][] | null | undefined,
  box: ImageBoxSize,
): number[][] | null {
  if (!Array.isArray(polygon) || polygon.length < 3) return null;
  if (!finitePositive(box.width) || !finitePositive(box.height)) return null;

  const scaled: number[][] = [];
  for (const point of polygon) {
    const x = Number(point?.[0]);
    const y = Number(point?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    scaled.push([x * box.width, y * box.height]);
  }
  return scaled.length >= 3 ? scaled : null;
}

/**
 * Displayed image rectangle inside a letterboxed object-fit:contain frame.
 * Fail closed when sizes are missing.
 */
export function displayedImageBoxForContain(args: {
  container: ImageBoxSize;
  natural: ImageBoxSize;
}): DisplayedImageBox | null {
  if (!finitePositive(args.container.width) || !finitePositive(args.container.height)) return null;
  if (!finitePositive(args.natural.width) || !finitePositive(args.natural.height)) return null;
  const rect = computeContainRect(
    args.container.width,
    args.container.height,
    args.natural.width,
    args.natural.height,
  );
  if (!finitePositive(rect.width) || !finitePositive(rect.height)) return null;
  return rect;
}

/**
 * Map a normalized camera polygon onto the actually displayed snapshot,
 * including object-fit contain letterboxing. Coordinates are relative to
 * the displayed image box, not the outer modal/container.
 */
export function scaleNormalizedPolygonToDisplayedImage(
  polygon: number[][] | null | undefined,
  args: {
    container: ImageBoxSize;
    natural: ImageBoxSize;
  },
): number[][] | null {
  const displayed = displayedImageBoxForContain(args);
  if (!displayed) return null;
  return scaleNormalizedPolygonToImageBox(polygon, displayed);
}
